import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Edit,
  Trash2,
  MessageSquare,
  Phone,
  FileText,
  ListChecks,
  KeyRound,
  ClipboardList
} from 'lucide-react';
import toast from 'react-hot-toast';
import { membersAPI, claimsAPI } from '../services/apiService';
import EnhancedClaimForm from './EnhancedClaimForm';
import UpdateClaimStatus from './UpdateClaimStatus';
import ActivityStatusModal from './ActivityStatusModal';
import ClaimBriefEscalateModal from './ClaimBriefEscalateModal';
import PortalsAccessModal from './PortalsAccessModal';
import ActionMenu from './ActionMenu';
import { sendWhatsAppMessage } from '../services/whatsappTemplates';
import { normalizeRequirementList } from '../services/pricingService';

const RequirementsCell = ({ requirements = [] }) => {
  if (!requirements.length) return <span className="text-slate-400">—</span>;
  const visible = requirements.slice(0, 3);
  const rest = requirements.slice(3);
  const full = requirements.join(', ');

  return (
    <div className="group relative max-w-[280px]">
      <div className="flex flex-wrap gap-1 items-center">
        {visible.map((req) => (
          <span
            key={req}
            className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-primary-100 text-primary-800"
          >
            {req.length > 22 ? `${req.slice(0, 20)}…` : req}
          </span>
        ))}
        {rest.length > 0 && (
          <span className="text-xs text-slate-500 font-medium cursor-default">+{rest.length}…</span>
        )}
      </div>
      <div className="pointer-events-none absolute left-0 top-full z-20 mt-1 hidden group-hover:block w-64 rounded-lg border border-slate-200 bg-white p-2 text-xs text-slate-700 shadow-lg">
        {full}
      </div>
    </div>
  );
};

const MemberDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEnhancedClaimForm, setShowEnhancedClaimForm] = useState(false);
  const [showUpdateStatus, setShowUpdateStatus] = useState(false);
  const [showActivityStatus, setShowActivityStatus] = useState(false);
  const [showBriefEscalate, setShowBriefEscalate] = useState(false);
  const [showPortals, setShowPortals] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);

  const fetchMemberData = useCallback(async () => {
    try {
      const response = await membersAPI.getById(id);
      if (response.member) {
        setMember(response.member);
        setClaims(response.claims || []);
      } else {
        toast.error('Member not found');
        navigate('/adminportal/members');
      }
    } catch (error) {
      console.error('Error fetching member data:', error);
      navigate('/adminportal/members');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchMemberData();
  }, [fetchMemberData]);

  const handleDeleteClaim = async (claimId, requirement) => {
    if (window.confirm(`Delete claim #${claimId}${requirement ? ` (${requirement})` : ''}?`)) {
      try {
        await claimsAPI.delete(claimId);
        toast.success('Claim deleted');
        fetchMemberData();
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleWhatsAppUpdate = (claim) => {
    if (!member.whatsapp) {
      toast.error('WhatsApp link not available');
      return;
    }
    const url = sendWhatsAppMessage(member.phone_number, claim, member);
    if (url) window.open(url, '_blank');
    else toast.error('Unable to generate WhatsApp message');
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      Completed: 'status-completed',
      'In Progress': 'status-in-progress',
      'Work in Progress': 'status-in-progress',
      'Not Started': 'status-not-started',
      'On Hold': 'status-on-hold'
    };
    return (
      <span className={`status-badge ${statusClasses[status] || 'status-not-started'}`}>
        {status || 'Not Started'}
      </span>
    );
  };

  const formatCurrency = (amount) => `₹${parseFloat(amount || 0).toLocaleString('en-IN')}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!member) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Member not found</p>
      </div>
    );
  }

  const infoRows = [
    ['SL No', member.sl_no || '—'],
    ['Name', member.name],
    ['UAN', member.uan],
    ['UAN Password', member.uan_password || '—'],
    ['Portal Password', member.password || '—'],
    ['Referred By', member.referred_by || '—'],
    ['Phone', member.phone_number || '—'],
    ['WhatsApp', member.whatsapp ? 'Available' : 'Not available']
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => navigate('/adminportal/members')} className="text-slate-600 hover:text-slate-800">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-slate-900">{member.name}</h1>
          <p className="text-sm text-slate-500">UAN {member.uan}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4 items-start">
        {/* Left: Member information card */}
        <aside className="card-compact space-y-4 lg:sticky lg:top-20">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-900">Member Information</h2>
            <button
              type="button"
              onClick={() => navigate(`/adminportal/members/${id}/edit`)}
              className="text-primary-600 hover:text-primary-700"
              title="Edit member"
            >
              <Edit className="w-4 h-4" />
            </button>
          </div>
          <dl className="space-y-2.5">
            {infoRows.map(([label, value]) => (
              <div key={label}>
                <dt className="text-[11px] uppercase tracking-wide text-slate-500">{label}</dt>
                <dd className="text-sm font-medium text-slate-900 break-all flex items-center gap-1.5">
                  {label === 'Phone' && member.phone_number && (
                    <a href={`tel:${member.phone_number}`} className="text-primary-600"><Phone className="w-3.5 h-3.5" /></a>
                  )}
                  {label === 'WhatsApp' && member.whatsapp && (
                    <MessageSquare className="w-3.5 h-3.5 text-green-600" />
                  )}
                  {value}
                </dd>
              </div>
            ))}
          </dl>

          <div className="pt-3 border-t border-slate-200 space-y-2">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">Portal credentials</div>
            <button
              type="button"
              onClick={() => setShowPortals(true)}
              className="w-full btn-secondary inline-flex items-center justify-center text-sm"
            >
              <KeyRound className="w-4 h-4 mr-1.5" />
              Portals Access
            </button>
            {(member.portal_credentials || []).length > 0 && (
              <p className="text-[11px] text-slate-500">
                {(member.portal_credentials || []).length} portal(s) saved
              </p>
            )}
          </div>
        </aside>

        {/* Right: Claims — single-row table */}
        <section className="card-compact min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h2 className="text-sm font-semibold text-slate-900">PF Claims</h2>
            <button
              type="button"
              onClick={() => setShowEnhancedClaimForm(true)}
              className="btn-primary inline-flex items-center text-sm"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              Add New Claim
            </button>
          </div>

          {claims.length === 0 ? (
            <div className="text-center py-10">
              <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">No claims yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-3 whitespace-nowrap">Claim ID</th>
                    <th className="py-2 pr-3">Requirements</th>
                    <th className="py-2 pr-3 whitespace-nowrap">Brief</th>
                    <th className="py-2 pr-3 whitespace-nowrap">Status</th>
                    <th className="py-2 pr-3 whitespace-nowrap">Fees</th>
                    <th className="py-2 pr-3 whitespace-nowrap">Balance</th>
                    <th className="py-2 pl-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {claims.map((claim) => {
                    const requirements = normalizeRequirementList(
                      Array.isArray(claim.requirements)
                        ? claim.requirements
                        : (claim.requirement ? claim.requirement.split(',').map((r) => r.trim()).filter(Boolean) : [])
                    );
                    return (
                      <tr key={claim.id} className="border-b border-slate-100 hover:bg-violet-50/40">
                        <td className="py-2.5 pr-3 font-mono text-xs font-semibold text-primary-700 whitespace-nowrap">
                          #{claim.id}
                        </td>
                        <td className="py-2.5 pr-3">
                          <RequirementsCell requirements={requirements} />
                        </td>
                        <td className="py-2.5 pr-3 whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedClaim(claim);
                              setShowBriefEscalate(true);
                            }}
                            className="inline-flex items-center gap-1 rounded-md border border-primary-200 bg-primary-50 px-2 py-1 text-[11px] font-medium text-primary-800 hover:bg-primary-100"
                            title="Case brief & escalate drafts"
                          >
                            <ClipboardList className="w-3.5 h-3.5" />
                            Brief
                          </button>
                        </td>
                        <td className="py-2.5 pr-3 whitespace-nowrap">
                          {getStatusBadge(claim.final_status || claim.status)}
                        </td>
                        <td className="py-2.5 pr-3 whitespace-nowrap">{formatCurrency(claim.fees)}</td>
                        <td className="py-2.5 pr-3 whitespace-nowrap text-green-700 font-medium">
                          {formatCurrency(claim.balance)}
                        </td>
                        <td className="py-2.5 pl-2 text-right">
                          <div className="inline-flex justify-end">
                            <ActionMenu
                              items={[
                                {
                                  label: 'Case Study',
                                  icon: Edit,
                                  onClick: () => {
                                    setSelectedClaim(claim);
                                    setShowUpdateStatus(true);
                                  }
                                },
                                {
                                  label: 'Update Claim Status',
                                  icon: ListChecks,
                                  onClick: () => {
                                    setSelectedClaim(claim);
                                    setShowActivityStatus(true);
                                  }
                                },
                                {
                                  label: 'WhatsApp Update',
                                  icon: MessageSquare,
                                  onClick: () => handleWhatsAppUpdate(claim)
                                },
                                {
                                  label: 'Delete',
                                  icon: Trash2,
                                  danger: true,
                                  onClick: () => handleDeleteClaim(claim.id, claim.requirement)
                                }
                              ]}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {showEnhancedClaimForm && (
        <EnhancedClaimForm
          memberId={parseInt(id, 10)}
          memberName={member.name}
          member={member}
          onClose={() => setShowEnhancedClaimForm(false)}
          onSuccess={() => fetchMemberData()}
        />
      )}

      {showUpdateStatus && selectedClaim && (
        <UpdateClaimStatus
          mode="update"
          claim={selectedClaim}
          member={member}
          onClose={() => {
            setShowUpdateStatus(false);
            setSelectedClaim(null);
          }}
          onSuccess={() => fetchMemberData()}
        />
      )}

      {showActivityStatus && selectedClaim && (
        <ActivityStatusModal
          claim={selectedClaim}
          member={member}
          onClose={() => {
            setShowActivityStatus(false);
            setSelectedClaim(null);
          }}
          onSuccess={() => fetchMemberData()}
        />
      )}

      {showBriefEscalate && selectedClaim && (
        <ClaimBriefEscalateModal
          claim={selectedClaim}
          member={member}
          onClose={() => {
            setShowBriefEscalate(false);
            setSelectedClaim(null);
          }}
          onSuccess={() => fetchMemberData()}
        />
      )}

      {showPortals && (
        <PortalsAccessModal
          member={member}
          onClose={() => setShowPortals(false)}
          onSaved={(cleaned) => {
            setMember((m) => ({ ...m, portal_credentials: cleaned }));
            fetchMemberData();
          }}
        />
      )}
    </div>
  );
};

export default MemberDetails;
